/**
 * PaymentsAllocationsPage.tsx
 *
 * Product Allocations — payments broken out per product line rather than per
 * payment, so a payment of Botox + Filler + Consult shows as three rows each
 * carrying its own amount.
 *
 * One row is one line AS PURCHASED. Partially disbursing a line splits its
 * payment_materials row and copies the whole-line total onto each half, so the
 * server collapses those fragments back together and re-sums their quantity
 * into the Qty column — see the grain notes on PaymentAllocationsService.
 *
 * Ports the legacy tab at payment_credit_list-test.php?pageID=4 and its
 * Product/Category view toggle (pageID=5). Legacy's ten columns plus Qty, the
 * same eleven sidebar filters, and an export carrying the legacy fifteen-column
 * set plus Qty.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download } from 'lucide-react';
import {
  getAllocationCategories,
  getAllocations,
  type AllocationCategoryRow,
  type AllocationRow,
} from '@/services/payments.service';
import { usePaymentFilterOptions } from '@/hooks/filters';
import { FilterSidebar } from '@/components/layout/FilterSidebar';
import { ListPageLayout } from '@/components/layout/ListPageLayout';
import { PageHeader } from '@/components/layout/PageHeader';
import { DataTable } from '@/components/ui/DataTable';
import type { DataTableColumn } from '@/components/ui/DataTable';
import { Button } from '@/components/ui/Button';
import { PatientLink } from '@/components/ui/PatientLink';
import { PaymentStatusBadge } from '@/components/payments/PaymentStatusBadge';
import { usePageContext } from '@/hooks/usePageContext';
import { useRoleTitles } from '@/hooks/useRoleTitles';
import { useAsyncExport } from '@/hooks/useAsyncExport';
import { useUserTimezone } from '@/hooks/useUserTimezone';
import {
  buildPaymentFilterConfigs,
  formatCurrency,
} from '@/pages/payments/paymentsShared';
import { buildApiParams } from '@/lib/filterRegistry';
import {
  useFacilityOverrideValues,
  useLocalDashboardFilters,
} from '@/pages/dashboard/dashboardFilters';
import { getTimezoneAbbreviation, toLocaleDateStringInTz, toLocaleTimeStringInTz } from '@/lib/facilityTimezone';

// Legacy's page size for this grid (assets-ftp/js/nav-test.js:2797-2809).
const PER_PAGE = 100;

/** Product grain vs Category grain — legacy's `.tab-select` dropdown. */
type AllocationView = 'product' | 'category';

const VIEW_TABS: { key: AllocationView; label: string }[] = [
  { key: 'product', label: 'Product' },
  { key: 'category', label: 'Category' },
];

function CompactDate({ value, tz }: { value: string | null | undefined; tz: string }) {
  if (!value) return <span className="text-slate-400">—</span>;
  const d = new Date(value);
  if (isNaN(d.getTime())) return <span className="text-xs">{value}</span>;
  const dateLine = toLocaleDateStringInTz(d, 'en-US', { month: 'short', day: 'numeric', year: '2-digit', timeZone: tz });
  const time = toLocaleTimeStringInTz(d, 'en-US', { hour: 'numeric', minute: '2-digit', timeZone: tz });
  const tzAbbr = getTimezoneAbbreviation(tz, d);
  return (
    <span className="flex flex-col items-start leading-tight whitespace-nowrap">
      <span className="text-[11px]">{dateLine}</span>
      <span className="text-[10px] text-slate-400">{time} {tzAbbr}</span>
    </span>
  );
}

/** "Last, First" — legacy blanks the cell entirely when there is no first name. */
function personName(first: string | null, last: string | null): string {
  const f = (first ?? '').trim();
  const l = (last ?? '').trim();
  if (!f) return '';
  return l ? `${l}, ${f}` : f;
}

export default function PaymentsAllocationsPage() {
  const userTz = useUserTimezone();
  const updateAIContext = usePageContext('Payments');

  const { roles } = useRoleTitles();
  const doctorLabel = roles.find((r) => r.roleType === 'Doctor')?.roleTitle || 'Doctor';
  const coordLabel = roles.find((r) => r.roleType === 'Coordinator')?.roleTitle || 'Coordinator';

  const filterConfigs = useMemo(
    () => buildPaymentFilterConfigs(doctorLabel, coordLabel),
    [doctorLabel, coordLabel],
  );

  const filterState = useLocalDashboardFilters(filterConfigs, 'payments-allocations');
  const { effectiveValues, isFacilityLocked } = useFacilityOverrideValues(filterState.values);
  const filters = useMemo(
    () => buildApiParams(effectiveValues, filterState.negations),
    [effectiveValues, filterState.negations],
  );

  const sidebarOptions = usePaymentFilterOptions();

  const [view, setView] = useState<AllocationView>('product');
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState<string>('');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const handleSortChange = useCallback((key: string, direction: 'asc' | 'desc') => {
    setSortBy(key);
    setSortDir(direction);
    setPage(1);
  }, []);

  useEffect(() => { setPage(1); }, [filters]);

  const { exporting, startExport, exportModal } = useAsyncExport({
    entityType: 'allocations',
    entityLabel: 'allocations',
  });

  // ─── Queries ──────────────────────────────────────────────────────────

  const { data: allocationsData, isLoading: allocationsLoading } = useQuery({
    queryKey: ['payment-allocations', filters, page, sortBy, sortDir],
    queryFn: () =>
      getAllocations(filters, {
        page,
        pageSize: PER_PAGE,
        sortBy: sortBy || undefined,
        sortDir: sortBy ? sortDir : undefined,
      }),
    staleTime: 30_000,
    enabled: view === 'product',
  });

  const { data: categoryData, isLoading: categoryLoading } = useQuery({
    queryKey: ['payment-allocation-categories', filters],
    queryFn: () => getAllocationCategories(filters),
    staleTime: 30_000,
    enabled: view === 'category',
  });

  const rows = allocationsData?.data ?? [];
  const categoryRows = categoryData?.data ?? [];
  const total = allocationsData?.meta.total ?? 0;

  // Both views report the same summed allocation amount, so switching view
  // never changes the headline figure.
  const allocationTotal =
    view === 'product'
      ? allocationsData?.meta.allocationTotal
      : categoryData?.meta.allocationTotal;

  const exportDisabled =
    exporting || (view === 'product' ? total === 0 : categoryRows.length === 0);

  // ─── AI page context ──────────────────────────────────────────────────

  const activeFilters = useMemo(
    () =>
      Object.entries(effectiveValues)
        .filter(([key, val]) => {
          if (key === 'search') return false;
          if (Array.isArray(val)) return val.length > 0;
          if (typeof val === 'object' && val !== null && 'from' in val) return !!(val.from || val.to);
          return false;
        })
        .map(([key]) => key)
        .join(', '),
    [effectiveValues],
  );

  useEffect(() => {
    updateAIContext({
      filters: activeFilters || 'no active filters',
      dataSummary:
        view === 'product'
          ? `${rows.length} allocations shown of ${total} total, page ${page}`
          : `${categoryRows.length} product categories`,
    });
  }, [activeFilters, view, rows.length, categoryRows.length, total, page, updateAIContext]);

  // ─── Columns: Product grain ───────────────────────────────────────────

  const allocationColumns = useMemo<DataTableColumn<AllocationRow>[]>(() => [
    {
      key: 'displayDate',
      header: 'Date',
      width: 'w-[78px] whitespace-nowrap',
      sortable: true,
      render: (row) => <CompactDate value={row.displayDate} tz={userTz} />,
    },
    {
      key: 'patientID',
      header: 'Patient',
      width: 'min-w-[150px]',
      sortable: true,
      render: (row) => {
        const name = personName(row.patientFirstName, row.patientLastName);
        if (!row.patientID) return <span className="text-slate-400">—</span>;
        return (
          <PatientLink
            patientId={row.patientID}
            patientName={name || `#${row.patientID}`}
            className="text-blue-600 hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            {name || `#${row.patientID}`}
          </PatientLink>
        );
      },
    },
    {
      key: 'typeLabel',
      header: 'Type',
      sortable: true,
      // Legacy renders this as a single "Type (Method)" cell; the method goes on
      // its own muted line rather than adding an eleventh column.
      render: (row) => (
        <span className="flex flex-col leading-tight">
          <span className="text-xs capitalize">{row.typeLabel || '—'}</span>
          {row.strPayMethod && (
            <span className="text-[10px] capitalize text-slate-400">{row.strPayMethod}</span>
          )}
        </span>
      ),
    },
    {
      key: 'allocationAmount',
      header: 'Amount',
      width: 'w-[90px] whitespace-nowrap',
      align: 'right',
      sortable: true,
      render: (row) => (
        <span className="font-medium text-slate-800">{formatCurrency(row.allocationAmount)}</span>
      ),
    },
    {
      key: 'transStatus',
      header: 'Status',
      width: 'w-[86px]',
      sortable: true,
      render: (row) => (
        <PaymentStatusBadge
          payment={{ void: row.void, transStatus: row.transStatus }}
          voidDate={row.voidDate}
          voidUserName={personName(row.voidUserFirstName, row.voidUserLastName) || null}
        />
      ),
    },
    {
      key: 'product',
      header: 'Product',
      width: 'min-w-[150px]',
      sortable: true,
      render: (row) => (
        <span className="text-xs line-clamp-3" title={row.product ?? undefined}>
          {row.product || '—'}
        </span>
      ),
    },
    {
      key: 'quantity',
      // Not sortable: the server's sort map has no `quantity` key, and an
      // unmapped key falls back to the default sort rather than erroring.
      header: 'Qty',
      width: 'w-[52px] whitespace-nowrap',
      align: 'right',
      render: (row) => <span className="text-xs tabular-nums">{row.quantity || 1}</span>,
    },
    {
      key: 'category',
      header: 'Category',
      sortable: true,
      render: (row) => (
        <span className="text-xs text-slate-500" title={row.category ?? undefined}>
          {row.category || '—'}
        </span>
      ),
    },
    {
      key: 'facilityName',
      header: 'Facility',
      sortable: true,
      render: (row) => <span className="text-xs text-slate-500">{row.facilityName || '—'}</span>,
    },
    {
      key: 'strMemo',
      header: 'Notes',
      sortable: true,
      render: (row) => (
        <span className="text-xs line-clamp-3" title={row.strMemo ?? undefined}>
          {row.strMemo || '—'}
        </span>
      ),
    },
    {
      key: 'userLastName',
      header: 'User',
      sortable: true,
      render: (row) => {
        const name = personName(row.userFirstName, row.userLastName);
        return (
          <span className="flex flex-col leading-tight">
            {/* No user means the charge arrived through a public payment link. */}
            <span className="text-xs">{name || <em className="text-slate-400">Payment Link</em>}</span>
            {row.enteredDate && (
              <span className="text-[10px] text-slate-400">entered {row.enteredDate}</span>
            )}
          </span>
        );
      },
    },
  ], [userTz]);

  // ─── Columns: Category grain ──────────────────────────────────────────

  const categoryColumns = useMemo<DataTableColumn<AllocationCategoryRow>[]>(() => [
    {
      key: 'category',
      header: 'Category',
      width: 'min-w-[200px]',
      render: (row) => <span className="text-sm text-slate-800">{row.category}</span>,
    },
    {
      key: 'allocations',
      header: 'Allocations',
      width: 'w-[110px]',
      align: 'right',
      render: (row) => <span className="text-xs">{row.allocations.toLocaleString()}</span>,
    },
    {
      key: 'totalAmount',
      header: 'Amount',
      width: 'w-[120px] whitespace-nowrap',
      align: 'right',
      render: (row) => (
        <span className="font-medium text-slate-800">{formatCurrency(row.totalAmount)}</span>
      ),
    },
    {
      key: 'giftCredits',
      header: 'Gift Credits',
      width: 'w-[120px] whitespace-nowrap',
      align: 'right',
      render: (row) => (
        <span className="text-xs text-purple-600">
          {row.giftCredits > 0 ? formatCurrency(row.giftCredits) : '—'}
        </span>
      ),
    },
  ], []);

  // ─── Render ───────────────────────────────────────────────────────────

  return (
    <ListPageLayout
      filterSidebar={(
        <FilterSidebar
          filters={filterConfigs}
          values={effectiveValues}
          options={sidebarOptions}
          negations={filterState.negations}
          onChange={filterState.setFilter}
          onNegateChange={filterState.setNegation}
          onClear={filterState.clearFilter}
          searchCommitMode="blur-or-enter"
          showAIAssistant={false}
          storageKey="payments-allocations"
          lockedKeys={isFacilityLocked ? ['facility'] : undefined}
        />
      )}
    >
      <div className="flex h-full min-h-0 flex-col">
        <div className="shrink-0 p-6">
          <PageHeader
            variant="compact"
            title="Allocations"
            actions={
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  leftIcon={<Download className="h-4 w-4" />}
                  onClick={() => startExport(filters)}
                  disabled={exportDisabled}
                  loading={exporting}
                >
                  Export
                </Button>
                {exportModal}
              </div>
            }
          />

          <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
            <div className="inline-flex rounded-lg bg-slate-100 p-1">
              {VIEW_TABS.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  aria-pressed={view === tab.key}
                  onClick={() => { setView(tab.key); setPage(1); }}
                  className={`rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${
                    view === tab.key
                      ? 'bg-white text-slate-900 shadow-sm'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {allocationTotal != null && (
              <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-2">
                <span className="text-xs font-medium text-slate-600">Total Allocated</span>
                <div className="text-lg font-semibold text-slate-900">
                  {formatCurrency(allocationTotal)}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {view === 'product' ? (
            <DataTable<AllocationRow>
              data={rows}
              columns={allocationColumns}
              keyField="rowKey"
              tableFixed
              loading={allocationsLoading}
              page={page}
              pageSize={PER_PAGE}
              total={total}
              onPageChange={setPage}
              // Server-paginated: DataTable's built-in client sort would only
              // reorder the current page, and is a no-op for render-based columns.
              manualSort
              sortKey={sortBy || null}
              sortDir={sortDir}
              onSortChange={handleSortChange}
              emptyTitle="No allocations found"
              emptyDescription="Try adjusting your filters."
            />
          ) : (
            <DataTable<AllocationCategoryRow>
              data={categoryRows}
              columns={categoryColumns}
              keyField="category"
              loading={categoryLoading}
              emptyTitle="No allocations found"
              emptyDescription="Try adjusting your filters."
            />
          )}
        </div>
      </div>
    </ListPageLayout>
  );
}
